import {
  FinalityProviderLogo,
  ValidatorSelector,
  type ColumnProps,
  IconButton,
} from "@babylonlabs-io/core-ui";
import { useMemo, useRef } from "react";
import { AiOutlinePlus } from "react-icons/ai";

import snowFallLogo from "@/ui/common/assets/SnowFall-logo-w.png";
import { useFinalityProviderBsnState } from "@/ui/common/state/FinalityProviderBsnState";
import { FinalityProviderStateLabels } from "@/ui/common/types/finalityProviders";
import { getNetworkConfigBTC } from "@/ui/common/config/network/btc";
import { satoshiToBtc } from "@/ui/common/utils/btc";
import { maxDecimals } from "@/ui/common/utils/maxDecimals";
import { formatCommissionPercentage } from "@/ui/common/utils/formatCommissionPercentage";
import { Hash } from "@/ui/common/components/Hash/Hash";
import {
  isSnowFallFinalityProvider,
  isSnowFallMoniker,
} from "@/ui/common/constants/snowfall";

interface Props {
  open: boolean;
  defaultFinalityProvider?: string;
  selectedBsnId?: string;
  onClose: () => void;
  onAdd: (selectedBsnId: string, selectedProviderKey: string) => void;
  onBack?: () => void;
}

export const FinalityProviderModal = ({
  open,
  selectedBsnId,
  onClose,
  onAdd,
  onBack,
}: Props) => {
  const {
    modalTitle,
    finalityProviders,
    filterOptions,
    filter,
    handleFilter,
    isRowSelectable,
  } = useFinalityProviderBsnState();

  const { coinSymbol } = getNetworkConfigBTC();

  const sortedFinalityProviders = useMemo(() => {
    const list = [...finalityProviders];
    list.sort((a, b) => {
      const aIsSnowFall = isSnowFallFinalityProvider(a);
      const bIsSnowFall = isSnowFallFinalityProvider(b);
      if (aIsSnowFall === bIsSnowFall) return 0;
      return aIsSnowFall ? -1 : 1;
    });
    return list;
  }, [finalityProviders]);

  const fpById = useMemo(() => {
    const map = new Map<string, (typeof finalityProviders)[number]>();
    for (const fp of sortedFinalityProviders) map.set(fp.btcPk, fp);
    return map;
  }, [sortedFinalityProviders]);

  const rows = useMemo(
    () =>
      sortedFinalityProviders.map((fp) => ({
        id: fp.btcPk,
        name: fp.description?.moniker || "No name provided",
        logo_url:
          fp.logo_url || (isSnowFallFinalityProvider(fp) ? snowFallLogo : ""),
        apr: "",
        votingPower: "",
        commission: formatCommissionPercentage(Number(fp.commission) || 0),
        rank: fp.rank,
      })),
    [sortedFinalityProviders],
  );

  const columns: ColumnProps<any>[] = [
    {
      key: "provider",
      header: "Finality Provider",
      headerClassName: "max-w-[240px]",
      cellClassName: "text-primary-dark max-w-[240px]",
      render: (
        _: unknown,
        row: { id: string; name: string; rank: number; logo_url: string },
      ) => {
        const original = sortedFinalityProviders.find(
          (fp) => fp.btcPk === row.id,
        );
        const rank = original
          ? sortedFinalityProviders.indexOf(original) + 1
          : 0;
        const isSnowFall = original
          ? isSnowFallFinalityProvider(original)
          : isSnowFallMoniker(row.name);
        return (
          <div className="flex min-w-0 items-center gap-2">
            <FinalityProviderLogo
              logoUrl={row.logo_url}
              rank={rank}
              moniker={row.name}
            />
            <span className="truncate">{row.name}</span>
            {isSnowFall ? (
              <span className="tracking-wide rounded-full bg-accent-primary/10 px-2 py-0.5 text-[0.625rem] font-semibold uppercase text-accent-primary">
                Featured
              </span>
            ) : null}
          </div>
        );
      },
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      key: "btcPk",
      header: "BTC PK",
      headerClassName: "max-w-[220px]",
      cellClassName: "max-w-[220px]",
      render: (_: unknown, row: { id: string }) => (
        <div
          className="truncate"
          // stop propagation to prevent selection of row
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <Hash value={String(row.id)} address small noFade />
        </div>
      ),
    },
    {
      key: "totalDelegation",
      header: "Total Delegation",
      headerClassName: "max-w-[160px]",
      render: (_: unknown, row: { id: string }) => {
        const fp = fpById.get(String(row.id));
        const total = maxDecimals(satoshiToBtc(fp?.activeTVLSat || 0), 8);
        return (
          <span className="inline-block max-w-[160px] truncate text-left">
            {total} {coinSymbol}
          </span>
        );
      },
      cellClassName: "pr-4 max-w-[160px]",
      sorter: (a: { id: string }, b: { id: string }) => {
        const fa = fpById.get(String(a.id));
        const fb = fpById.get(String(b.id));
        const va = fa?.activeTVLSat ?? 0;
        const vb = fb?.activeTVLSat ?? 0;
        return va - vb;
      },
    },
    {
      key: "commission",
      header: "Commission",
      headerClassName: "max-w-[140px]",
      cellClassName: "pr-4 max-w-[140px]",
      render: (_: unknown, row: { commission: string }) => (
        <span className="inline-block max-w-[140px] truncate text-left">
          {row.commission}
        </span>
      ),
      sorter: (a: { commission: string }, b: { commission: string }) =>
        Number.parseFloat(a.commission) - Number.parseFloat(b.commission),
    },
    {
      key: "action",
      header: "",
      render: () => (
        <IconButton size="medium">
          <AiOutlinePlus size={18} className="text-accent-primary" />
        </IconButton>
      ),
    },
  ];

  const closingFromAddRef = useRef(false);

  const handleClose = () => {
    if (closingFromAddRef.current) {
      closingFromAddRef.current = false;
      return;
    }
    onClose();
  };

  const handleSelect = (row: any) => {
    if (selectedBsnId !== undefined) {
      closingFromAddRef.current = true;
      onAdd(selectedBsnId, String(row.id));
    }
  };

  const mapGridItem = (row: any) => {
    const original = sortedFinalityProviders.find((fp) => fp.btcPk === row.id);
    const rank = original ? sortedFinalityProviders.indexOf(original) + 1 : 0;
    const fp = fpById.get(String(row.id));

    if (!fp) {
      return {
        providerItemProps: {
          bsnId: "",
          bsnName: "",
          address: String(row.id),
          provider: { rank: rank, description: { moniker: row.name } },
        },
        attributes: {},
      };
    }
    const totalDelegation = maxDecimals(satoshiToBtc(fp.activeTVLSat || 0), 8);
    const commission = formatCommissionPercentage(Number(fp.commission) || 0);
    const status = FinalityProviderStateLabels[fp.state] || "Unknown";
    return {
      providerItemProps: {
        bsnId: fp.bsnId || "",
        bsnName: fp.chain || "",
        address: fp.btcPk,
        provider: {
          logo_url:
            fp.logo_url || (isSnowFallFinalityProvider(fp) ? snowFallLogo : ""),
          rank: rank,
          description: fp.description,
        },
        showChain: false,
      },
      attributes: {
        Status: status,
        "Total Delegation": (
          <>
            {totalDelegation} {coinSymbol}
          </>
        ),
        Commission: commission,
      },
    };
  };

  const handleIsRowSelectable = (row: any) => {
    const fp = fpById.get(String(row.id));
    return fp ? isRowSelectable(fp as any) : false;
  };

  return (
    <ValidatorSelector
      open={open}
      validators={rows}
      columns={columns}
      onClose={handleClose}
      onSelect={handleSelect}
      title={modalTitle}
      description="Finality Providers play a key role in securing Proof-of-Stake networks by validating and finalising transactions. Select one to delegate your stake."
      onBack={onBack}
      defaultLayout="list"
      gridItemMapper={mapGridItem}
      isRowSelectable={handleIsRowSelectable}
      filters={{
        options: filterOptions,
        value: filter.providerStatus,
        onSelect: (value) => handleFilter("providerStatus", String(value)),
      }}
    />
  );
};
